import { useState } from 'react';

import { dashboardT } from '../../../../lib/messages';
import type { TargetingFormValue } from '../../model';
import type { TargetingFormUpdate } from './TargetingForm.types';
import {
  moveSelectionOnInclusionToggle,
  toSupplyApps,
  toTailoredSupplies,
} from './TargetingSupplyFields.helpers';
import { ClassicDomainsTargetingSection } from './ClassicDomainsTargetingSection';
import { InclusionToggle } from './InclusionToggle';
import { ManageTailoredSupplyDialog } from './ManageTailoredSupplyDialog';
import { ManagedSelectionSection } from './ManagedSelectionSection';
import { SelectedAppsTable, SelectedTailoredSupplyTable } from './SelectedSupplyTables';
import { ShigoAppsSelectionDialog } from './ShigoSupplySelectionDialogs';

enum ShigoSupplyDialog {
  Apps = 'APPS',
  TailoredSupply = 'TAILORED_SUPPLY',
}

export function ShigoTargetingSupplyFields({
  showApps,
  showDomains,
  showTailoredSupply,
  value,
  onChange,
}: {
  showApps: boolean;
  showDomains: boolean;
  showTailoredSupply: boolean;
  value: TargetingFormValue;
  onChange: (value: TargetingFormValue) => void;
}) {
  const [openDialog, setOpenDialog] = useState<ShigoSupplyDialog | null>(null);
  const updateField: TargetingFormUpdate = (key, nextValue) => {
    onChange({ ...value, [key]: nextValue });
  };

  return (
    <>
      {showApps ? (
        <ManagedSelectionSection
          title={dashboardT('supply.apps')}
          selectedItems={value.appsIncluded ? value.includedApps : value.excludedApps}
          manageLabel={dashboardT('targeting.manageApps')}
          clearLabel={dashboardT('targeting.removeAllApps')}
          emptyText={dashboardT('targeting.noAppsSelectedRunAll')}
          onManage={() => {
            setOpenDialog(ShigoSupplyDialog.Apps);
          }}
          onClear={() => {
            onChange({ ...value, includedApps: [], excludedApps: [] });
          }}
          action={
            <InclusionToggle
              included={value.appsIncluded}
              onIncludedChange={(included) => {
                const next = moveSelectionOnInclusionToggle({
                  included,
                  includedItems: value.includedApps,
                  excludedItems: value.excludedApps,
                });
                onChange({
                  ...value,
                  appsIncluded: included,
                  includedApps: next.includedItems,
                  excludedApps: next.excludedItems,
                });
              }}
            />
          }
        >
          <SelectedAppsTable
            items={value.appsIncluded ? value.includedApps : value.excludedApps}
            showCompany={false}
          />
        </ManagedSelectionSection>
      ) : null}

      {showDomains ? (
        <ClassicDomainsTargetingSection updateField={updateField} value={value} />
      ) : null}

      {showTailoredSupply ? (
        <ManagedSelectionSection
          title={dashboardT('targeting.tailoredSupply')}
          selectedItems={
            value.tailoredSuppliesIncluded
              ? value.includedTailoredSupplies
              : value.excludedTailoredSupplies
          }
          manageLabel={dashboardT('targeting.manageTailoredSupply')}
          emptyText={dashboardT('targeting.noTailoredSupplySelectedRunAll')}
          onManage={() => {
            setOpenDialog(ShigoSupplyDialog.TailoredSupply);
          }}
          onClear={() => {
            onChange({
              ...value,
              includedTailoredSupplies: [],
              excludedTailoredSupplies: [],
              tailoredSupplyExcludedApps: [],
              tailoredSupplyExcludedDomains: [],
            });
          }}
          action={
            <InclusionToggle
              included={value.tailoredSuppliesIncluded}
              onIncludedChange={(included) => {
                const next = moveSelectionOnInclusionToggle({
                  included,
                  includedItems: value.includedTailoredSupplies,
                  excludedItems: value.excludedTailoredSupplies,
                });
                onChange({
                  ...value,
                  tailoredSuppliesIncluded: included,
                  includedTailoredSupplies: next.includedItems,
                  excludedTailoredSupplies: next.excludedItems,
                });
              }}
            />
          }
        >
          <SelectedTailoredSupplyTable
            items={
              value.tailoredSuppliesIncluded
                ? value.includedTailoredSupplies
                : value.excludedTailoredSupplies
            }
            supplyType={value.supplyType}
            excludedApps={value.tailoredSupplyExcludedApps}
            excludedDomains={value.tailoredSupplyExcludedDomains}
            onExcludedAppsChange={(items) => {
              updateField('tailoredSupplyExcludedApps', items);
            }}
            onExcludedDomainsChange={(items) => {
              updateField('tailoredSupplyExcludedDomains', items);
            }}
          />
        </ManagedSelectionSection>
      ) : null}

      {openDialog === ShigoSupplyDialog.Apps ? (
        <ShigoAppsSelectionDialog
          selectedItems={toSupplyApps(value.appsIncluded ? value.includedApps : value.excludedApps)}
          onOpenChange={(open) => {
            setOpenDialog(open ? ShigoSupplyDialog.Apps : null);
          }}
          onConfirm={(apps) => {
            updateField(value.appsIncluded ? 'includedApps' : 'excludedApps', apps);
          }}
        />
      ) : null}
      {openDialog === ShigoSupplyDialog.TailoredSupply ? (
        <ManageTailoredSupplyDialog
          open
          selectedItems={toTailoredSupplies(
            value.tailoredSuppliesIncluded
              ? value.includedTailoredSupplies
              : value.excludedTailoredSupplies,
          )}
          onOpenChange={(open) => {
            setOpenDialog(open ? ShigoSupplyDialog.TailoredSupply : null);
          }}
          onConfirm={(tailoredSupplies) => {
            updateField(
              value.tailoredSuppliesIncluded
                ? 'includedTailoredSupplies'
                : 'excludedTailoredSupplies',
              tailoredSupplies,
            );
          }}
        />
      ) : null}
    </>
  );
}
